// Single opener for the app's "LucasAI" IndexedDB. v1 held only the durable
// recording queue (useIndexDb.js). v2 adds the offline-mode caches (spec §2).
// ONE module owns open/upgrade so the recording queue and the offline caches
// can never race each other with mismatched versions — both consumers share
// the same connection via the module-level singleton promise.
export const LUCAS_DB_VERSION = 2;

// Promise wrappers over the raw IDB request/transaction events, shared by
// every consumer of this database (the recording queue in useIndexDb.js and
// the offline caches in offlineCache.js) so the two can't drift on error
// handling.
export const promisifyRequest = (request) =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = (event) => reject(event.target?.error || event);
  });

// Resolve once a write transaction has durably COMMITTED (oncomplete), not when
// the individual request succeeds — that is the write-ahead guarantee callers
// rely on. On abort/error prefer transaction.error (the actual cause, e.g.
// QuotaExceededError on an abort) over event.target.error, which is often null.
export const awaitTransaction = (transaction) =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = (event) => reject(transaction.error || event.target?.error || event);
    transaction.onabort = (event) => reject(transaction.error || event.target?.error || event);
  });

let dbPromise = null;

// Everything that must happen to a connection before callers touch it, shared by the
// normal open and the version-skew fallback below.
const adoptConnection = (db, resolve, { alreadyResolvedNull = false } = {}) => {
  // Release this connection on a FUTURE upgrade (e.g. another tab after a deploy
  // bumps LUCAS_DB_VERSION) so this tab doesn't become the one blocking everyone
  // else, per the onblocked guard below. Drop the singleton too: keeping a closed
  // handle would make every later openLucasDb() hand out a dead connection.
  // (useIndexDb re-opens on demand — see its ensureDb.)
  db.onversionchange = () => {
    db.close();
    dbPromise = null;
  };
  if (alreadyResolvedNull) {
    // Callers were already handed the no-IDB null because the open was blocked; the
    // blocking tab has since closed and the upgrade went through. Republish the live
    // connection so the NEXT openLucasDb() gets a working db instead of a stale null.
    dbPromise = Promise.resolve(db);
    return;
  }
  resolve(db);
};

const openFreshLucasDb = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      resolve(null);
      return;
    }
    const request = window.indexedDB.open("LucasAI", LUCAS_DB_VERSION);
    request.onerror = (event) => {
      const error = event.target?.error;
      // VersionError: this bundle asked for an OLDER version than the database on
      // disk. Two bundles on one origin is the normal state of the world for a while
      // after any deploy — a tab restored from the back/forward cache, an app shell
      // still in the HTTP cache, the Chrome extension side panel holding yesterday's
      // build — and whichever one is older then cannot open the database AT ALL.
      // Seen in production on iPhone (LUCASHEALTH-V7), where it left the recorder
      // with no queue for the session.
      //
      // Attach to whatever version IS there instead. Schema changes here only ADD
      // stores, so a newer database is a superset: every store this build knows about
      // is present. Safe only because a VersionError proves the database exists — a
      // versionless open on a MISSING database would create an empty one with no
      // stores at all.
      if (error && error.name === "VersionError") {
        console.error("IndexedDB is at a newer version than this build expects; attaching to the existing one");
        const fallback = window.indexedDB.open("LucasAI");
        fallback.onerror = (fallbackEvent) => {
          console.error("IndexedDB error:", fallbackEvent.target?.error);
          resolve(null);
        };
        fallback.onsuccess = (fallbackEvent) => adoptConnection(fallbackEvent.target.result, resolve);
        return;
      }
      console.error("IndexedDB error:", error);
      resolve(null); // callers already handle a null db (no offline cache / queue)
    };
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      // v1 store — MUST keep the exact shape useIndexDb.js relies on.
      if (!db.objectStoreNames.contains("recordings")) {
        const store = db.createObjectStore("recordings", { autoIncrement: true });
        store.createIndex("userId", "userId");
      }
      // v2 offline caches. cacheKey = `${scope}|${id}`; scope = `${userId}|${orgId}`
      // so rows are hard-partitioned per user+org (shared-device safety, spec §2).
      if (!db.objectStoreNames.contains("patients")) {
        db.createObjectStore("patients", { keyPath: "cacheKey" }).createIndex("scope", "scope");
      }
      if (!db.objectStoreNames.contains("chats")) {
        db.createObjectStore("chats", { keyPath: "cacheKey" }).createIndex("scope", "scope");
      }
      if (!db.objectStoreNames.contains("offlineMeta")) {
        db.createObjectStore("offlineMeta", { keyPath: "key" });
      }
    };
    // A stale tab holding an open connection at the old version blocks this
    // upgrade (review finding M7). The request stays pending until that tab
    // closes, so waiting on it would hang every consumer: the Patients page
    // would spin forever and recordings would sit behind an unresolved
    // promise. Resolve null instead — that is the documented "no IndexedDB"
    // mode every caller already handles.
    let resolvedNull = false;
    request.onblocked = () => {
      // console.ERROR, not warn: this is the failure that took three weeks and a
      // Sentry dig to identify, precisely because a warn is invisible there
      // (captureConsoleIntegration is levels:"error"). Every caller sees a null
      // database — the recorder's queue included — so it is reportable, not chatter.
      // Callers re-open on demand (useIndexDb's ensureDb), so this is no longer
      // terminal for the session; it is still worth knowing how often a deploy
      // lands on a clinician with an older tab open.
      console.error("IndexedDB open blocked — an older tab is still holding a connection at a previous version");
      resolvedNull = true;
      resolve(null);
    };
    request.onsuccess = (event) => {
      adoptConnection(event.target.result, resolve, { alreadyResolvedNull: resolvedNull });
    };
  });

export const openLucasDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = openFreshLucasDb();
  return dbPromise;
};

// Test-only escape hatch. Nulling the module singleton alone is not enough:
// fake-indexeddb's backing store is shared by every connection opened within
// the same test FILE (it only resets between files), so a second `open()`
// call would just reconnect to the previous test's data. Close the prior
// connection (a pending delete stalls behind an open one) and delete the
// underlying database before the next `openLucasDb()` reopens it, so each
// test that calls this starts from a genuinely empty store. Assigning the
// chained promise synchronously — not just awaiting it — means any
// `openLucasDb()` call made before this settles still resolves in order,
// even though most call sites (e.g. `beforeEach(() => { __resetLucasDbForTests(); ... })`)
// don't await this function's return value. Not used by production code.
export const __resetLucasDbForTests = () => {
  const priorDbPromise = dbPromise;
  dbPromise = (async () => {
    if (priorDbPromise) {
      try {
        const db = await priorDbPromise;
        db?.close();
      } catch (_e) {
        // prior connection already failed/closed — nothing to clean up
      }
    }
    if (typeof window !== "undefined" && window.indexedDB?.deleteDatabase) {
      await new Promise((resolve) => {
        const request = window.indexedDB.deleteDatabase("LucasAI");
        request.onsuccess = () => resolve();
        request.onerror = () => resolve();
        request.onblocked = () => resolve();
      });
    }
    return openFreshLucasDb();
  })();
  return dbPromise;
};
